import { createReducer } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash'

import { TCommentApi } from 'types/GlobalComment'

import * as commentActions from '../../actions/Comments/actions'
import * as authenticationActions from '../../actions/Authentication/actions'
import { EReduxState } from 'redux/utils/State'

export interface CommentReducerType {
  data: Record<string, TCommentApi>
  byKeyMetricId: Record<string, string[]>
  byReportEditionId: Record<string, string[]>
  byAnalysisId: Record<string, string[]>
  state: EReduxState
}

const INITIAL_STATE: CommentReducerType = {
  data: {},
  byKeyMetricId: {},
  byReportEditionId: {},
  byAnalysisId: {},
  state: EReduxState.IDLE
}

const commentReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    // tryGetAllComments
    .addCase(commentActions.getAll, (state) => {
      state.state = EReduxState.LOADING
    })
    .addCase(commentActions.getAllSuccess, (state, action) => {
      const { data, keyMetricId, reportEditionId, analysisId } = action.payload

      data.forEach((comment) => {
        state.data[comment.id] = comment
      })

      const rootIds = data.filter((c) => c.parent_id === null).map((c) => c.id)

      if (keyMetricId) {
        state.byKeyMetricId[keyMetricId] = rootIds
      }

      if (reportEditionId) {
        state.byReportEditionId[reportEditionId] = rootIds
      }

      if (analysisId) {
        state.byAnalysisId[analysisId] = rootIds
      }

      state.state = EReduxState.SUCCESS
    })
    .addCase(commentActions.getAllFailed, (state) => {
      state.state = EReduxState.ERROR
    })

    // tryCreateComment
    .addCase(commentActions.createSuccess, (state, action) => {
      const comment = action.payload.data

      state.data[comment.id] = comment

      if (comment.key_metric_id) {
        const existing = state.byKeyMetricId[comment.key_metric_id] ?? []

        if (!comment.parent_id) {
          state.byKeyMetricId[comment.key_metric_id] = [...existing, comment.id]
        }
      }

      if (comment.analysis_id && !comment.parent_id) {
        const existing = state.byAnalysisId[comment.analysis_id] ?? []

        state.byAnalysisId[comment.analysis_id] = [...existing, comment.id]
      }
    })

    // tryUpdateComment
    .addCase(commentActions.updateSuccess, (state, action) => {
      const comment = action.payload.data

      state.data[comment.id] = comment
    })

    // tryDeleteComment
    .addCase(commentActions.destroySuccess, (state, action) => {
      const id = action.payload
      const comment = state.data[id]

      if (comment) {
        const newData = cloneDeep(state.data)

        delete newData[id]
        state.data = newData

        if (comment.key_metric_id && !comment.parent_id) {
          state.byKeyMetricId[comment.key_metric_id] = (
            state.byKeyMetricId[comment.key_metric_id] ?? []
          ).filter((cid) => cid !== id)
        }

        if (comment.analysis_id && !comment.parent_id) {
          state.byAnalysisId[comment.analysis_id] = (
            state.byAnalysisId[comment.analysis_id] ?? []
          ).filter((cid) => cid !== id)
        }
      }
    })

    // Sign out
    .addCase(authenticationActions.signOut, () => INITIAL_STATE)
    .addDefaultCase((state) => state)
})

export default commentReducer
